import type { EntityOnSaveProps } from "@firecms/core";

import {
    collectionStringsFor,
    currentCmsLocale,
} from "../i18n/collection-strings";
import { describeOutcomes, rebuildTargets, triggerRebuilds } from "./rebuild";

/**
 * The `onSaveSuccess` callback for every collection whose records are
 * prerendered pages.
 *
 * Shared rather than written per collection so `news` and `tags` cannot drift
 * apart — a tag's label renders as a chip on every article that carries it, and
 * retiring one removes pages, so a tag edit changes the site exactly as much as
 * an article edit does.
 *
 * **Runs on success only, and never blocks the save.** It is attached to
 * `onSaveSuccess`, so the record is already written by the time this fires;
 * throwing here would tell an editor their save failed when it did not. Every
 * failure becomes a snackbar that says the record is saved and only the rebuild
 * needs retrying.
 */
export async function onSaveRebuild({
    context,
}: EntityOnSaveProps): Promise<void> {
    if (rebuildTargets.length === 0) {
        // The normal state under `pnpm dev:cms` with no hooks in `.env`. Logged
        // rather than shown, because a local editor has no site to rebuild and
        // a snackbar on every save would be noise.
        console.info(
            "[rebuild] no deploy hooks configured — skipping. Set " +
                "VITE_DEPLOY_HOOK_DEVELOP to rebuild the develop site on save.",
        );
        return;
    }

    // FireCMS calls this as a plain callback, not from a component, so the
    // locale comes from the key the i18n provider persists rather than a hook.
    const strings = collectionStringsFor(currentCmsLocale()).rebuild;
    const described = describeOutcomes(await triggerRebuilds(), strings);
    if (described) context.snackbarController.open(described);
}
